export const AMPLITUDE_MIN_USER_ID_LENGTH = 5;

/**
 * Placeholders some IdPs return instead of a real subject. Amplitude folds every
 * user sharing an id into one profile, so tracking any of these silently merges
 * unrelated people into a single account.
 */
const RESERVED_USER_IDS = new Set([
  "-1",
  "0",
  "00000000-0000-0000-0000-000000000000",
  "anonymous",
  "guest",
  "n/a",
  "na",
  "nil",
  "none",
  "null",
  "undefined",
  "unknown",
  "{}",
]);

/**
 * Returns the id to identify a user by, or null when it is missing, too short or
 * a placeholder. The console and the sandbox both use this so a placeholder
 * cannot be handled in one app and missed in the other.
 */
export const normaliseUserId = (raw?: string | null): string | null => {
  const candidate = raw?.trim();

  if (!candidate || candidate.length < AMPLITUDE_MIN_USER_ID_LENGTH) {
    return null;
  }

  return RESERVED_USER_IDS.has(candidate.toLowerCase()) ? null : candidate;
};

export const getErrorStatus = (error: unknown): number | undefined =>
  error && typeof error === "object" && "status" in error
    ? (error as { status: number }).status
    : undefined;

export const AMPLITUDE_SERVER_ZONE = "EU";

export const BASE_AMPLITUDE_INIT_OPTIONS = {
  serverZone: AMPLITUDE_SERVER_ZONE,
  identityStorage: "localStorage",
  trackingOptions: { ipAddress: false },
  autocapture: {
    pageViews: { trackHistoryChanges: "pathOnly" },
    webVitals: false,
    elementInteractions: false,
    frustrationInteractions: false,
    fileDownloads: false,
    formInteractions: false,
  },
} as const;

/**
 * Reads a Session Replay sample rate from an env value and clamps it to [0, 1].
 * An unset or non-numeric value means "record nothing". Shared so the console
 * and the standalone sandbox interpret the setting the same way.
 */
export const parseSampleRate = (raw?: string | number): number => {
  if (raw === undefined || raw === "") return 0;

  const rate = Number(raw);

  return Number.isFinite(rate) ? Math.min(Math.max(rate, 0), 1) : 0;
};

/**
 * Replay runs only when its flag is on and some share of sessions is sampled.
 * A flag with a zero rate records nothing, so both apps treat it as off.
 */
export const isSessionReplayEnabled = (
  replayEnabled: boolean,
  sampleRate: number,
): boolean => replayEnabled && sampleRate > 0;

/** Every tracked event records its outcome, and a failure records the status. */
export type Outcome = { outcome: "success" | "failure"; error_status?: number };

/** How an app hands an event to its SDK: straight away, or after a lazy load. */
export type AmplitudeSender<EventMap extends object> = <
  E extends keyof EventMap,
>(
  event: E,
  properties: EventMap[E],
) => void | Promise<void>;

/** Events carrying nothing beyond the outcome take no details argument. */
type DetailsArgs<
  EventMap extends object,
  E extends keyof EventMap,
> = keyof Omit<EventMap[E], keyof Outcome> extends never
  ? []
  : [details: Omit<EventMap[E], keyof Outcome>];

/**
 * Builds the tracking helpers over one app's event map. The event shape and the
 * send-and-report behaviour are shared; only the transport differs, which is why
 * the caller passes it in.
 */
export const createAmplitudeTracking = <EventMap extends object>(
  send: AmplitudeSender<EventMap>,
) => {
  const track = async <E extends keyof EventMap>(
    event: E,
    properties: EventMap[E],
  ): Promise<void> => {
    try {
      await send(event, properties);
    } catch (error) {
      console.warn(`Failed to track "${String(event)}"`, error);
    }
  };

  /**
   * Runs an action and records whether it succeeded. The original error is
   * always rethrown, so wrapping an action never changes how it fails.
   */
  const withTracking = async <E extends keyof EventMap, T>(
    event: E,
    run: () => Promise<T>,
    ...[details]: DetailsArgs<EventMap, E>
  ): Promise<T> => {
    try {
      const result = await run();

      await track(event, { ...details, outcome: "success" } as EventMap[E]);

      return result;
    } catch (error) {
      await track(event, {
        ...details,
        outcome: "failure",
        error_status: getErrorStatus(error),
      } as EventMap[E]);

      throw error;
    }
  };

  return { track, withTracking };
};
